import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output } from "@angular/core";
import { queryStringForAlertResult } from "@app2/alert/alert-inbox/utils";
import { Domain } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { SearchClientService } from "@app2/clients/search-client.service";
import { InboxAlert } from "@app2/clients/types/alert-typedefs";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { FeaturesService } from "@app2/shared/services/features.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { RouterService } from "@app2/shared/services/router.service";
import { TimeService } from "@app2/shared/services/time.service";
import { SavedQuery, SearchRangeRequest } from "@app2/type-defs/search/search-types";
import { byField } from "@app2/util/util";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { orderBy, uniq, uniqBy } from "lodash-es";
import { getMessageForError } from "@app2/util/errors/handleable-errors";

export interface AlertStoryEntry {
    expanded: boolean;
    alert: InboxAlert;
    data: AlertStoryEntryData[];
}

interface AlertStoryEntryData {
    orderKey: string;
    key: string;
    value: any[];
    important: boolean;
    expanded: boolean;
}

const presetImportantFields = [
    "ip",
    "user_name",
    "email",
    "phone_number",
];

@UntilDestroy()
@Component({
    selector: "alert-story-alerts-table",
    template: `
        <table *dsLoading="loadingTable"
               class="hover-table full-width alerts-table"
               test-id="alert-story-table">
            <thead>
                <tr>
                    <th class="hover-tab"></th>
                    <th class="chevron" *featureGate="'alertStoryView'"></th>
                    <th>{{ "TRIGGER_NAME" | localize | uppercase }}</th>
                    <th class="short-cell center">{{ "COUNT" | localize}}</th>
                    <th class="timestamp-cell center">{{ "DURING" | localize}}</th>
                    <th class="short-cell center">{{ "STATE" | localize}}</th>
                    <th class="short-cell"></th>
                </tr>
            </thead>
            <tbody *ngFor="let entry of entries">
            <tr class="declutter"
                (click)="showDetails(entry)"
                test-class="alert-story-events">
                <td class="hover-tab"></td>
                <td class="chevron" *featureGate="'alertStoryView'">
                    <i class="far"
                       [ngClass]="{'fa-chevron-up': entry.expanded, 'fa-chevron-down': !entry.expanded}"></i>
                </td>
                <td>
                    <a (click)="showTriggerView(entry.alert); $event.stopPropagation()"
                       [matTooltip]="'GO_TO_TRIGGER' | localize"
                       class="link-text"
                       test-id="trigger-link">
                        {{ entry.alert.trigger.name }}
                    </a>
                    <a (click)="showAlertView(entry.alert); $event.stopPropagation()"
                       [matTooltip]="'GO_TO_ALERT' | localize"
                       class="link-text alert-bell show-on-hover"
                       test-id="alert-link">
                        <i class="far fa-bell"></i>
                    </a>
                </td>
                <td class="short-cell center">
                    <search-link [queryString]="queryStringForAlertResult(entry.alert.trigger, savedQueries[entry.alert.trigger.savedQueryId], entry.alert)"
                                 [begin]="entry.alert.resultTimestamp"
                                 [end]="entry.alert.resultEndTimestamp"
                                 [filterField]="entry.alert.trigger.filterField"
                                 [filterListId]="entry.alert.trigger.filterListId"
                                 [excludeField]="entry.alert.trigger.excludeField"
                                 [excludeListId]="entry.alert.trigger.excludeListId"
                                 [defaultText]="false"
                                 [newTab]="true"
                                 [matTooltip]="'INBOX_VIEW_EVENTS' | localize">
                        {{ entry.alert.resultCount }}
                    </search-link>
                </td>
                <td class="timestamp-cell center">
                    <span class="interval-container">{{ entry.alert.resultTimestamp | shortDateAndTime }}</span>
                    &ndash;
                    <span class="interval-container">{{ entry.alert.resultEndTimestamp | shortDateAndTime }}</span>
                </td>
                <td class="short-cell center">
                    {{ entry.alert.currentState | localize }}
                </td>
                <td class="short-cell center show-on-hover">
                    <single-alert-state-changer *ngIf="entry.alert.currentState !== 'ESCALATED'"
                                                (onStateChanged)="refreshAlerts()"
                                                [alertId]="entry.alert.id"
                                                [currentState]="entry.alert.currentState"
                                                [resultTimestamp]="entry.alert.resultTimestamp"
                                                [trigger]="entry.alert.trigger"
                                                [escalateOnly]="true">
                    </single-alert-state-changer>
                </td>
            </tr>
            <ng-container *featureGate="'alertStoryView'">
            <tr *ngIf="entry.expanded"
                test-class="alert-story-details-list"
                class="no-hover">
                <td class="alert-story-details" *dsLoading="loadingDetails">
                    <ul class="alert-story-details-list" *ngIf="entryData">
                        <li *ngFor="let datum of entryData; let i = index"
                            (click)="datum.expanded = !datum.expanded"
                            class="alert-story-details-dropdown"
                            test-class="alert-story-details-dropdown">
                            <span test-class="alert-story-details-key">
                                <i class="far"
                                   [ngClass]="{'fa-chevron-up': datum.expanded, 'fa-chevron-down': !datum.expanded}"></i>
                                {{ datum.key }}
                                <ng-container *ngIf="datum.value.length > 1">
                                    ({{ datum.value.length }})
                                </ng-container>
                            </span>
                            <ul *ngIf="datum.expanded" (click)="$event.stopPropagation()">
                                <li *ngFor="let value of datum.value">
                                    <span class="alert-story-details-value"
                                          test-class="alert-story-details-value">
                                        {{ value }}
                                    </span>
                                </li>
                            </ul>
                        </li>
                    </ul>
                    <span *ngIf="!entryData"
                          class="details-error-text">
                        <i class="far fa-exclamation-circle"></i>
                        {{ "ALERT_STORY_DETAILS_ERROR" | localize }}
                    </span>
                </td>
            </tr>
            </ng-container>
            </tbody>
            <tbody *ngIf="!alerts?.length">
            <tr>
                <td class="no-data-text" colspan="5">{{ 'INBOX_NO_ALERTS_FOR_TRIGGER' | localize }}</td>
            </tr>
            </tbody>
        </table>
    `,
})
export class AlertStoryAlertsTableComponent implements OnChanges, OnInit {
    @Input() aggregateValue: string;
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onNewAndAckedAlertIdsChanged = new EventEmitter<{alerts: InboxAlert[], alertIds: uuid[], sampleTriggerId: uuid}>();

    alerts: InboxAlert[];
    loadingTable: boolean = true;
    loadingDetails: boolean = true;
    savedQueries: Record<uuid, SavedQuery> = {};
    entries: AlertStoryEntry[];
    entryData: AlertStoryEntryData[];
    featureEnabled: boolean;

    private importantFields: string[] = presetImportantFields;

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private alertService: AlertClientService,
                private searchService: SearchClientService,
                private notificationsService: NotificationsService,
                private routerService: RouterService,
                private savedQueryService: SavedQueryService,
                private featuresService: FeaturesService,
                private timeService: TimeService) {
    }

    ngOnInit() {
        this.savedQueryService.getSavedQueries$()
            .pipe(untilDestroyed(this))
            .subscribe((savedQueries: Record<uuid, SavedQuery>) => this.savedQueries = savedQueries);
        this.featuresService.getFeature$("alertStoryView")
            .pipe(untilDestroyed(this))
            .subscribe(feature => this.featureEnabled = feature);
    }

    ngOnChanges() {
        if (this.aggregateValue) {
            this.refreshAlerts();
        }
    }

    refreshAlerts() {
        this.loadingTable = true;
        // ask search-service for alertDerivatives "defensestorm_type:alert AND this.aggregateValue"
        const searchRequest: SearchRangeRequest = {
            queryString: `defensestorm_type:alert AND _exists_:fired_alert_result_id AND "${this.aggregateValue}"`,
            begin: this.timeService.now().add(-14, "d"),
            end: this.timeService.now(),
            pageSize: 50,
            sortField: "timestamp",
            sortDesc: true,
        };

        Promise
            .all([
                this.getSearchAlerts(searchRequest),
                this.alertService.getAlertsForAggregateValue(this.aggregateValue, 100)])
            .then( data => {
                this.alerts = orderBy(uniqBy(data.flat(), "id"), ["resultTimestamp"], ["desc"]);
                this.entries = this.alerts.map(alert => {
                    return Object.assign({ alert: alert, expanded: false });
                });
                const filteredAlerts =
                    this.alerts.filter(a => a.currentState === "NEW" || a.currentState === "ACKED").slice(0,100);
                this.onNewAndAckedAlertIdsChanged.emit(
                    {
                        alerts: this.alerts,
                        alertIds: filteredAlerts.map(alert => alert.id),
                        sampleTriggerId: filteredAlerts.length !== 0 ? filteredAlerts[0].triggerId : null,
                    });
            })
            .catch(error => {
                const errorMessage = getMessageForError(error.error, "ERROR_LOADING_ALERTS");
                this.notificationsService.showError(errorMessage, error);
            })
            .finally(() => this.loadingTable = false);
    }

    private getSearchAlerts(searchRequest: SearchRangeRequest): Promise<InboxAlert[]> {
        return this.searchService.searchRange(searchRequest)
            .then(response => {
                const alertDerivativeIds = uniq(response.hits.map(h => h.fired_alert_result_id).flat());
                return alertDerivativeIds.length > 0
                    ? this.alertService.getAlertDetailsList(alertDerivativeIds)
                    : [];
            });
    }

    showTriggerView(alert: InboxAlert) {
        this.routerService.navigate([], {
            mode: "trigger",
            sort: "newest_first",
            state: "NEW",
            dropdownFilter: "all",
            selected: alert.triggerId,
        }, {}, { openInNewTab: true });
    }

    showAlertView(alert: InboxAlert) {
        this.routerService.navigate([], {
            mode: "alert",
            sort: "newest_first",
            state: "NEW",
            dropdownFilter: "all",
            selected: alert.id,
        }, {}, { openInNewTab: true });
    }

    showDetails(entry: AlertStoryEntry) {
        if (this.featureEnabled) {
            entry.expanded = !entry.expanded;
        }
        if (entry.expanded) {
            const alert = entry.alert;
            this.loadingDetails = true;
            const searchRequest = AlertStoryAlertsTableComponent.topNHitsQuery(`fired_alert_result_id=${ alert.id }`, alert.resultEndTimestamp, alert.resultEndTimestamp);
            this.searchService.searchRange(searchRequest)
                .then(entries => {
                    this.entryData = this.getDisplayableData(entries.hits[0]);
                })
                .catch(err => {
                    this.entryData = null;
                    const message = getMessageForError(err.error, "ERROR_LOADING_SEARCH_RANGE");
                    this.notificationsService.showError(message, err);
                })
                .finally(() => this.loadingDetails = false);
        }
    }

    queryStringForAlertResult = queryStringForAlertResult;

    private static topNHitsQuery(queryString: string, begin, end): SearchRangeRequest {
        return {
            queryString,
            begin,
            end,
            pageNumber: 1,
            pageSize: 20,
            sortField: "timestamp",
            sortDesc: true,
        };
    }

    //TODO: Refactor with code from SearchResultTable Component
    private getDisplayableData(unmodifiedEntry: object) {
        return Object.entries(unmodifiedEntry)
            .map(([field, value]) => {
                if (!Array.isArray(value)) {
                    value = [value];
                }
                const important = this.importantFields.includes(field);
                const orderKey = (<string>field).split(".", 2)[0]; // so that nested objects retain their order
                return { orderKey: orderKey, key: field, important: important, value: value, expanded: important };
            })
            .filter(datum => datum.key !== "_id")
            .sort(byField("orderKey"))
            .sort((a, b): number => {
                return +b.important - +a.important;
            });
    }
}
